import {useCallback, useEffect, useRef, useState} from "react";

export const useSlider = (slidesCount: number, autoAdvanceInterval: number = 2500) => {
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [progress, setProgress] = useState<number>(0);
    const [isPaused, setIsPaused] = useState<boolean>(false);

    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const startTimeRef = useRef<number>(0);
    const remainingTimeRef = useRef<number>(autoAdvanceInterval);

    const goToNext = useCallback(() => {
        setActiveIndex(prev => (prev === slidesCount - 1 ? 0 : prev + 1));
    }, [slidesCount]);

    const goToPrev = useCallback(() => {
        setActiveIndex(prev => (prev === 0 ? slidesCount - 1 : prev - 1));
    }, [slidesCount]);

    const goToSlide = useCallback((index: number) => {
        if (index >= 0 && index < slidesCount) {
            setActiveIndex(index);
        }
    }, [slidesCount]);

    const pause = useCallback(() => {
        setIsPaused(true);
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        // Calculate remaining time
        const elapsed = Date.now() - startTimeRef.current;
        remainingTimeRef.current = Math.max(0, autoAdvanceInterval - elapsed);
    }, [autoAdvanceInterval]);

    const resume = useCallback(() => {
        setIsPaused(false);
    }, []);

    const resetTimer = useCallback(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        if (isPaused) return;

        setProgress(0);
        startTimeRef.current = Date.now();
        remainingTimeRef.current = autoAdvanceInterval;

        const timeout = isPaused ? remainingTimeRef.current : autoAdvanceInterval;

        timeoutRef.current = setTimeout(goToNext, timeout);
        intervalRef.current = setInterval(() => {
            const elapsed = Date.now() - startTimeRef.current;
            const progressPercent = Math.min(100, (elapsed / autoAdvanceInterval) * 100);
            setProgress(progressPercent);
        }, 50);
    }, [goToNext, autoAdvanceInterval, isPaused]);

    useEffect(() => {
        resetTimer();
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [resetTimer, activeIndex]);

    useEffect(() => {
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    return {
        activeIndex,
        progress,
        isPaused,
        goToNext,
        goToPrev,
        goToSlide,
        pause,
        resume
    };
};